import { useEffect, useRef, useState } from "react";
import { ScannerType } from "models/scanner-type";
import { isLowResCheckSupported } from "services/document-capture/scanner-capabilities";

const useDeviceCamera = (
  scannerType: ScannerType
): [MediaStream | undefined, boolean] => {
  const [isLowResolution, setIsLowResolution] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream>();
  const [lowResCheckSupported, minHeight] = isLowResCheckSupported(scannerType);
  const gettingCameraStream = useRef(false);

  useEffect(
    () => {
      if (gettingCameraStream.current) {
        return;
      }

      gettingCameraStream.current = true;

      const getCameraStream = async () => {
        const features = {
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        };

        return navigator.mediaDevices.getUserMedia(features);
      };

      if (lowResCheckSupported && minHeight !== undefined) {
        getCameraStream()
          .then((stream) => {
            const settings = stream.getVideoTracks()[0].getSettings();

            setIsLowResolution(
              settings.height === undefined || settings.height < minHeight
            );

            // This probe stream is only used to read the camera resolution
            // above — the active scanner opens its own camera. Release the
            // device immediately so it isn't held open for the whole scanning
            // session (which would otherwise keep the camera running even when
            // the page is backgrounded). We still expose the (now-stopped)
            // stream object as the "camera available" signal the capturers gate
            // on via `!cameraStream`.
            stream.getTracks().forEach((track) => track.stop());

            setCameraStream(stream);
          })
          .catch((_) => {
            setCameraStream(undefined);
            setIsLowResolution(false);
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return [cameraStream, isLowResolution];
};

export default useDeviceCamera;
